/**
 * localStorage access that degrades to a no-op. Inside a third-party iframe the
 * API can be missing entirely (Safari "Prevent cross-site tracking", Chrome
 * third-party storage partitioning in strict modes), and the widget must keep
 * working — it just loses the ability to resume a chat after a reload.
 */

function probeStorage(): Storage | null {
  try {
    const candidate = window.localStorage;
    const probe = '__t1-support-probe';
    candidate.setItem(probe, '1');
    candidate.removeItem(probe);
    return candidate;
  } catch {
    return null;
  }
}

const store = typeof window === 'undefined' ? null : probeStorage();

export const storageAvailable = store !== null;

export function readItem(key: string): string | null {
  try {
    return store ? store.getItem(key) : null;
  } catch {
    return null;
  }
}

export function writeItem(key: string, value: string): void {
  try {
    store?.setItem(key, value);
  } catch {
    /* quota or privacy error — resuming simply won't work */
  }
}

export function removeItem(key: string): void {
  try {
    store?.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Keys are namespaced per site so two T1 sites never share a conversation. */
export const storageKeys = {
  conversationId: (siteId: string) => `t1-support:${siteId}:conversationId`,
  /**
   * The conversation id reserved for a first message that has not been accepted
   * yet. It survives a reload, so resending after a failure reuses the same
   * ticket instead of opening a second one.
   */
  pendingConversationId: (siteId: string) => `t1-support:${siteId}:pendingConversationId`,
  visitorProfile: (siteId: string) => `t1-support:${siteId}:visitor`,
  seenAt: (siteId: string) => `t1-support:${siteId}:seenAt`,
};

export interface VisitorProfile {
  name: string;
  email: string;
}

export function readVisitorProfile(siteId: string): VisitorProfile | null {
  const raw = readItem(storageKeys.visitorProfile(siteId));
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as VisitorProfile).name === 'string' &&
      typeof (parsed as VisitorProfile).email === 'string'
    ) {
      return { name: (parsed as VisitorProfile).name, email: (parsed as VisitorProfile).email };
    }
  } catch {
    /* corrupt value — treat as absent */
  }
  return null;
}

export function writeVisitorProfile(siteId: string, profile: VisitorProfile): void {
  writeItem(storageKeys.visitorProfile(siteId), JSON.stringify(profile));
}
